"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  Bot,
  UploadCloud,
  CheckCircle,
  XCircle,
  Send,
  Bell,
  Sparkles,
  Building2,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  RefreshCw,
  Edit3,
  Check,
  AlertTriangle,
  FileText,
  Sliders,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CheckSquare,
  Square,
  Search,
  MessageCircle,
  Copy,
  FlaskConical,
  Save,
  SendHorizontal,
  Tag,
  ArrowDownToLine,
  Download,
} from "lucide-react";
import { OutreachQueueItem } from "@/lib/db";
import { Button } from "@/components/ui/Button";
import { parseGoogleMapsCsv, parseCsvRows, sanitizePhone } from "@/lib/csvParser";

interface StatsData {
  pendingCount: number;
  approvedCount: number;
  sentCount: number;
  repliedCount: number;
  rejectedCount: number;
  totalLeads: number;
}

interface WhatsAppQuota {
  sentThisMonth: number;
  monthlyLimit: number;
  remaining: number;
  isLimitReached: boolean;
  monthName: string;
}

interface AiSalesAgentTabProps {
  currentUser?: {
    id: number;
    username: string;
    role: string;
    fullName: string;
    companyName?: string;
  };
}

export default function AiSalesAgentTab({ currentUser }: AiSalesAgentTabProps = {}) {
  const [stats, setStats] = useState<StatsData>({
    pendingCount: 0,
    approvedCount: 0,
    sentCount: 0,
    repliedCount: 0,
    rejectedCount: 0,
    totalLeads: 0,
  });

  const [whatsAppQuota, setWhatsAppQuota] = useState<WhatsAppQuota>({
    sentThisMonth: 0,
    monthlyLimit: 990,
    remaining: 990,
    isLimitReached: false,
    monthName: "This Month",
  });

  const [activeFilter, setActiveFilter] = useState<"pending_approval" | "approved" | "sent" | "ALL">("pending_approval");
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState<OutreachQueueItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSelectingAllWhole, setIsSelectingAllWhole] = useState(false);
  const [approvingSelected, setApprovingSelected] = useState(false);
  const [dispatchingSelected, setDispatchingSelected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);

  // Meta Template Review Status State
  const [templateInfo, setTemplateInfo] = useState<{
    status: string;
    name: string;
    id?: string;
    category?: string;
    directUrl: string;
    loading: boolean;
  }>({
    status: "PENDING",
    name: "johnson_business_outreach",
    directUrl: "https://business.facebook.com/latest/whatsapp_manager/message_templates?business_id=2086184842020567",
    loading: false,
  });

  const fetchTemplateStatus = useCallback(async () => {
    setTemplateInfo((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch("/api/crm/whatsapp/template-status");
      const data = await res.json();
      if (data.success && data.template) {
        setTemplateInfo({
          status: data.template.status || "PENDING",
          name: data.template.name || "johnson_business_outreach",
          id: data.template.id,
          category: data.template.category,
          directUrl: data.directManagerUrl || "https://business.facebook.com/latest/whatsapp_manager/message_templates?business_id=2086184842020567",
          loading: false,
        });
      } else {
        setTemplateInfo((prev) => ({ ...prev, loading: false }));
      }
    } catch {
      setTemplateInfo((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchTemplateStatus();
  }, [fetchTemplateStatus]);

  // Modals / Drawers
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OutreachQueueItem | null>(null);

  // Individual Card Testing & Inline Saving State
  const [testingItemId, setTestingItemId] = useState<number | null>(null);
  const [dispatchingSingleId, setDispatchingSingleId] = useState<number | null>(null);
  const [savedNoticeId, setSavedNoticeId] = useState<number | null>(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<number | null>(null);
  const [editingCardPitches, setEditingCardPitches] = useState<{
    [id: number]: { whatsapp_pitch: string; email_subject: string; email_pitch: string };
  }>({});

  // Reclassify all pending leads into their respected industries
  const handleReclassifyAll = async () => {
    setReclassifying(true);
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reclassify_all" }),
      });
      const data = await res.json();
      if (data.success) {
        const breakdownStr = Object.entries(data.breakdown || {})
          .map(([cat, count]) => `• ${cat}: ${count}`)
          .join("\n");
        alert(`🎉 Success!\nRefreshed pitches for ${data.totalUpdated} leads into their respected industries!\n\nBreakdown:\n${breakdownStr}`);
        fetchData();
      } else {
        alert(`Error refreshing pitches: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setReclassifying(false);
    }
  };

  // Re-detect industry and regenerate pitch for a single lead
  const handleRegenerateSinglePitch = async (id: number) => {
    setRegeneratingId(id);
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "regenerate_single", id }),
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setRegeneratingId(null);
    }
  };

  // Import State
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importCampaignName, setImportCampaignName] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importTab, setImportTab] = useState<"upload" | "paste">("upload");
  const [parsedLeadPreviewCount, setParsedLeadPreviewCount] = useState<number>(0);

  const handleFileRead = (file: File) => {
    if (!file) return;
    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      let text = (e.target?.result as string) || "";
      if (text.charCodeAt(0) === 0xFEFF) {
        text = text.slice(1);
      }
      if (text) {
        setCsvText(text);
        let parsed = parseGoogleMapsCsv(text);
        if (parsed.length === 0) {
          const rows = parseCsvRows(text);
          if (rows.length >= 2) {
            const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));
            const leadsFound: any[] = [];
            for (let i = 1; i < rows.length; i++) {
              const values = rows[i];
              if (!values || values.length === 0) continue;
              const row: any = {};
              headers.forEach((header, idx) => {
                row[header] = values[idx] || "";
              });
              const businessName =
                row["company"] ||
                row["company name"] ||
                row["name"] ||
                row["buyer name"] ||
                row["agency name"] ||
                row["business name"] ||
                row["title"] ||
                values[0];
              const city = row["city"] || row["port"] || row["destination port"] || row["location"] || row["address"] || "Bangalore";
              const rawPhone = row["phone"] || row["whatsapp"] || row["mobile"] || row["contact"] || row["cell"] || "";
              const phone = sanitizePhone(rawPhone);
              const email = row["email"] || row["mail"] || row["email address"] || "";
              const contactName = row["contact name"] || row["contact person"] || row["owner"] || row["person"] || row["name"] || "";
              const category = row["commodity"] || row["product"] || row["service category"] || row["industry"] || row["category"] || row["service"] || "";
              if (businessName && businessName !== "Directions" && businessName !== "Website") {
                leadsFound.push({
                  businessName,
                  contactName: contactName !== businessName ? contactName : undefined,
                  city,
                  phone,
                  email,
                  website: "",
                  category,
                });
              }
            }
            parsed = leadsFound;
          }
        }
        setParsedLeadPreviewCount(parsed.length);
        if (!importCampaignName) {
          const cleanName = file.name.replace(/\.csv$/i, "").replace(/[-_]/g, " ");
          setImportCampaignName(cleanName.charAt(0).toUpperCase() + cleanName.slice(1));
        }
      }
    };
    reader.readAsText(file);
  };

  // Telegram Config State
  const [telegramStatus, setTelegramStatus] = useState<{ hasToken: boolean; hasChatId: boolean; isEnabled: boolean } | null>(null);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramTestResult, setTelegramTestResult] = useState<string | null>(null);

  // Dispatch State
  const [dispatching, setDispatching] = useState(false);
  const [dispatchResult, setDispatchResult] = useState<string | null>(null);

  // Prevent accidental tab closure or reload during active outreach dispatch
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dispatching) {
        e.preventDefault();
        e.returnValue = "Outreach dispatch is currently running! Reloading or closing will pause the remaining queue. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dispatching]);

  // Fetch Queue and Stats with Pagination
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== "ALL") params.set("status", activeFilter);
      if (searchTerm) params.set("search", searchTerm);
      params.set("limit", String(pageSize));
      params.set("offset", String((currentPage - 1) * pageSize));

      const res = await fetch(`/api/crm/outreach/queue?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        if (data.total !== undefined) setTotalCount(data.total);
        if (data.stats) setStats(data.stats);
        if (data.whatsAppQuota) setWhatsAppQuota(data.whatsAppQuota);
      }

      // Check Telegram status
      const tgRes = await fetch("/api/crm/telegram");
      if (tgRes.ok) {
        const tgData = await tgRes.json();
        setTelegramStatus(tgData);
      }
    } catch (err) {
      console.error("Error fetching outreach data:", err);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchTerm, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (filter: "pending_approval" | "approved" | "sent" | "ALL") => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handleSearchChange = (val: string) => {
    setSearchTerm(val);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Toggle individual card selection (persisted across pages)
  const toggleSelectOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Check if all items on current page are selected
  const currentPageIds = items.map((i) => i.id);
  const isCurrentPageAllSelected = items.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
  const hasSomeCurrentPageSelected = items.some((i) => selectedIds.has(i.id)) && !isCurrentPageAllSelected;

  // Toggle select all on current page
  const toggleSelectCurrentPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isCurrentPageAllSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Select all leads matching current queue/filter across ALL pages
  const handleSelectAllWholeQueue = async () => {
    setIsSelectingAllWhole(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== "ALL") params.set("status", activeFilter);
      if (searchTerm) params.set("search", searchTerm);
      params.set("ids_only", "true");

      const res = await fetch(`/api/crm/outreach/queue?${params.toString()}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.allIds)) {
        setSelectedIds(new Set(data.allIds));
      } else {
        alert(`Could not select all: ${data.error || "Unknown error"}`);
      }
    } catch (err: any) {
      alert(`Error selecting all leads: ${err.message}`);
    } finally {
      setIsSelectingAllWhole(false);
    }
  };

  // Deselect All (across all pages)
  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  // Approve all currently selected leads
  const handleApproveSelected = async () => {
    if (selectedIds.size === 0) return;
    setApprovingSelected(true);
    try {
      const targetIds = Array.from(selectedIds);
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve", ids: targetIds }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`🎉 Successfully approved ${data.changed || targetIds.length} lead(s) for outreach!`);
        setSelectedIds(new Set());
        await fetchData();
        setActiveFilter("approved");
        setCurrentPage(1);
      } else {
        alert(`Error approving selected: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setApprovingSelected(false);
    }
  };

  // Dispatch all currently selected leads in safe batches of 15
  const handleDispatchSelected = async () => {
    if (selectedIds.size === 0) return;
    const targetIds = Array.from(selectedIds);
    if (!confirm(`🚀 Are you sure you want to dispatch outreach messages to all ${targetIds.length} selected lead(s)?`)) {
      return;
    }

    setDispatchingSelected(true);
    setDispatching(true);
    setDispatchResult(`🚀 Preparing batch dispatch for ${targetIds.length} selected leads...`);

    try {
      // First ensure they are marked approved
      await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve", ids: targetIds }),
      });

      const chunkSize = 15;
      const chunks: number[][] = [];
      for (let i = 0; i < targetIds.length; i += chunkSize) {
        chunks.push(targetIds.slice(i, i + chunkSize));
      }

      let totalSent = 0;
      for (let c = 0; c < chunks.length; c++) {
        const chunkIds = chunks[c];
        setDispatchResult(`⏳ Dispatching batch ${c + 1} of ${chunks.length} (${totalSent}/${targetIds.length} sent)... Please keep this tab open.`);

        const res = await fetch("/api/crm/outreach/dispatch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: chunkIds }),
        });

        const contentType = res.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const data = await res.json();
          if (data.success) {
            totalSent += data.sentCount || chunkIds.length;
          }
        } else {
          totalSent += chunkIds.length;
        }

        if (c < chunks.length - 1) {
          await new Promise((r) => setTimeout(r, 600));
        }
      }

      setDispatchResult(`🎉 Dispatched to all ${totalSent} selected leads successfully!`);
      setSelectedIds(new Set());
      await fetchData();
      setActiveFilter("sent");
      setCurrentPage(1);
    } catch (err: any) {
      console.error("Batch dispatch error:", err);
      setDispatchResult(`⚠️ Dispatch paused. Unsent leads remain safely saved in queue.`);
      await fetchData();
    } finally {
      setDispatching(false);
      setDispatchingSelected(false);
    }
  };

  // Single Approve
  const handleApprove = async (id: number) => {
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve", id }),
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error("Error approving lead:", err);
    }
  };

  // Bulk Approve All Pending
  const handleApproveAll = async () => {
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve_all_pending" }),
      });
      if (res.ok) {
        await fetchData();
        setActiveFilter("approved");
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error approving all:", err);
    }
  };

  // Reject / Skip
  const handleReject = async (id: number) => {
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reject", id }),
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error("Error skipping lead:", err);
    }
  };

  // Save Edited Pitch
  const handleSavePitchEdit = async () => {
    if (!editingItem) return;
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "edit_pitch",
          id: editingItem.id,
          updates: {
            whatsapp_pitch: editingItem.whatsapp_pitch,
            email_subject: editingItem.email_subject,
            email_pitch: editingItem.email_pitch,
          },
        }),
      });
      if (res.ok) {
        setEditingItem(null);
        fetchData();
      }
    } catch (err) {
      console.error("Error saving pitch edit:", err);
    }
  };

  // Dispatch Approved Queue in safe batches to prevent 60s reverse proxy timeouts
  const handleDispatchApproved = async () => {
    if (stats.approvedCount === 0) {
      alert("No approved leads to dispatch. Please approve leads in the queue first!");
      return;
    }

    setDispatching(true);
    setDispatchResult("🚀 Preparing batch dispatch...");

    try {
      // Fetch currently approved leads to chunk them
      const queueRes = await fetch("/api/crm/outreach/queue?status=approved");
      const queueData = await queueRes.json();
      const approvedItems: OutreachQueueItem[] = queueData.items || [];

      if (approvedItems.length === 0) {
        setDispatchResult("No approved leads found to dispatch.");
        return;
      }

      // 15 leads per batch ensures each sub-request takes ~12-15s (well below 60s proxy limit)
      const chunkSize = 15;
      const chunks: number[][] = [];
      for (let i = 0; i < approvedItems.length; i += chunkSize) {
        chunks.push(approvedItems.slice(i, i + chunkSize).map((item) => item.id));
      }

      let totalSent = 0;

      for (let c = 0; c < chunks.length; c++) {
        const chunkIds = chunks[c];
        setDispatchResult(`⏳ Dispatching batch ${c + 1} of ${chunks.length} (${totalSent}/${approvedItems.length} sent)... Please keep this browser tab open.`);

        const res = await fetch("/api/crm/outreach/dispatch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: chunkIds }),
        });

        const contentType = res.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const data = await res.json();
          if (data.success) {
            totalSent += data.sentCount || chunkIds.length;
          }
        } else {
          totalSent += chunkIds.length;
        }

        if (c < chunks.length - 1) {
          await new Promise((r) => setTimeout(r, 600));
        }
      }

      setDispatchResult(`🎉 Dispatched to all ${totalSent} agencies successfully!`);
      await fetchData();
      setActiveFilter("sent");
    } catch (err: any) {
      console.error("Batch dispatch error:", err);
      setDispatchResult(`⚠️ Dispatch paused due to network or tab reload. Unsent leads remain safely preserved in "Approved & Queued". Click "Resume Outreach" anytime to continue without any duplicate messages!`);
      await fetchData();
    } finally {
      setDispatching(false);
    }
  };

  // Send Test Pitch directly to Johnson's phone & email
  const handleSendTestToMe = async (item: OutreachQueueItem) => {
    setTestingItemId(item.id);
    try {
      const res = await fetch("/api/crm/outreach/dispatch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ids: [item.id],
          testMode: true,
          testPhone: "+919620194983",
          testEmail: "johnsondev02@gmail.com",
        }),
      });
      const contentType = res.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await res.json() : { success: res.ok };
      if (data.success) {
        alert(`🧪 Test preview sent!\nCheck your WhatsApp (+91 9620194983) and Email (johnsondev02@gmail.com) for ${item.business_name}'s pitch.`);
      } else {
        alert(`⚠️ Test note: ${data.message || data.error}`);
      }
    } catch (err: any) {
      alert(`❌ Test dispatch error: ${err.message}`);
    } finally {
      setTestingItemId(null);
    }
  };

  // Dispatch live pitch for this single lead only
  const handleDispatchSingleLead = async (item: OutreachQueueItem) => {
    setDispatchingSingleId(item.id);
    try {
      const res = await fetch("/api/crm/outreach/dispatch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: [item.id] }),
      });
      const contentType = res.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await res.json() : { success: res.ok, sentCount: 1 };
      if (data.success && data.sentCount > 0) {
        const channels = data.results?.[0]?.channels?.join(" & ") || "WhatsApp & Email";
        alert(`🎉 Live Dispatch Sent!\n\nSuccessfully dispatched to ${item.business_name} via ${channels}.\n\nLead has been moved to "Dispatched" and added to your CRM Kanban board as a "New Lead"!`);
        fetchData();
      } else {
        const detail = data.results?.[0]?.error || data.error || "No channels dispatched successfully";
        alert(`⚠️ Dispatch note: ${detail}\n\nTip: You can click "Send Test to My Phone" to verify WhatsApp and Email delivery directly to your personal test mobile (+91 9620194983).`);
      }
    } catch (err: any) {
      alert(`❌ Dispatch Network Error: ${err.message}`);
    } finally {
      setDispatchingSingleId(null);
    }
  };

  // Reset any lead back to the Approved queue
  const handleResetToApproved = async (id: number) => {
    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve", ids: [id] }),
      });
      if (res.ok) {
        alert("✅ Lead reset to Approved queue! You can now test or dispatch again.");
        fetchData();
      }
    } catch (err) {
      console.error("Error resetting to approved:", err);
    }
  };

  // Save inline edits to card's WhatsApp pitch and Email template
  const handleSaveCardPitch = async (item: OutreachQueueItem) => {
    const edits = editingCardPitches[item.id] || {
      whatsapp_pitch: item.whatsapp_pitch || "",
      email_subject: item.email_subject || "",
      email_pitch: item.email_pitch || "",
    };

    try {
      const res = await fetch("/api/crm/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "edit_pitch",
          id: item.id,
          updates: {
            whatsapp_pitch: edits.whatsapp_pitch,
            email_subject: edits.email_subject,
            email_pitch: edits.email_pitch,
          },
        }),
      });
      if (res.ok) {
        setSavedNoticeId(item.id);
        setTimeout(() => setSavedNoticeId(null), 3500);
        fetchData();
      } else {
        alert("Failed to save pitch updates.");
      }
    } catch (err) {
      console.error("Error saving card pitch:", err);
      alert("Error saving pitch updates.");
    }
  };

  // CSV Import Processor
  const handleProcessCsvImport = async () => {
    if (!csvText.trim()) {
      alert("Please paste CSV data or load sample leads.");
      return;
    }

    setImporting(true);
    try {
      // Decode using specialized Google Maps Scraper Parser
      let leads = parseGoogleMapsCsv(csvText);

      // Fallback to RFC-4180 compliant row parser if specialized parser didn't find leads
      if (leads.length === 0) {
        const rows = parseCsvRows(csvText);
        if (rows.length >= 2) {
          const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));
          for (let i = 1; i < rows.length; i++) {
            const values = rows[i];
            if (!values || values.length === 0) continue;
            const row: any = {};
            headers.forEach((header, idx) => {
              row[header] = values[idx] || "";
            });
            const businessName =
              row["company"] ||
              row["company name"] ||
              row["name"] ||
              row["agency name"] ||
              row["business name"] ||
              row["title"] ||
              values[0];
            const city = row["city"] || row["port"] || row["destination port"] || row["location"] || row["address"] || "Bangalore";
            const rawPhone = row["phone"] || row["whatsapp"] || row["mobile"] || row["contact"] || row["cell"] || "";
            const phone = sanitizePhone(rawPhone);
            const email = row["email"] || row["mail"] || row["email address"] || "";
            const contactName = row["contact name"] || row["contact person"] || row["owner"] || row["person"] || row["name"] || "";
            const rating = row["rating"] || row["google rating"] ? parseFloat(row["rating"] || row["google rating"]) : undefined;
            const category = row["commodity"] || row["product"] || row["industry"] || row["category"] || row["service"] || "";
            if (businessName && businessName !== "Directions" && businessName !== "Website") {
              leads.push({
                businessName,
                contactName: contactName !== businessName ? contactName : undefined,
                city,
                phone,
                email,
                website: "",
                rating,
                category,
              });
            }
          }
        }
      }

      if (leads.length === 0) {
        alert("No valid leads detected from the CSV data. Please check the format.");
        setImporting(false);
        return;
      }

      const res = await fetch("/api/crm/outreach/import", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaignName: importCampaignName || `Outreach Campaign (${leads.length} Leads)`,
          leads,
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert(`🎉 Success! Imported ${data.importedCount} new unique prospects (${data.duplicateCount || 0} existing duplicates safely skipped).\nTelegram notification dispatched!`);
        setIsImportModalOpen(false);
        setCsvText("");
        fetchData();
      } else {
        alert(`Import error: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Error during import: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Load Sample Scraped Leads
  const loadSampleCsv = () => {
    const sample = 
`Business Name,City,Phone,Email,Rating
Apex Prime Realty,Bangalore,+919880893416,contact@apexrealty.in,4.8
Prestige Property Consultants,Mumbai,+919820123456,sales@prestigeproperties.com,4.7
Skyline Estates & Homes,Hyderabad,+919440198765,info@skylineestates.co.in,4.9
Urban Habitat Realtors,Delhi NCR,+919811234567,deals@urbanhabitat.in,4.6
Golden Key Real Estate,Pune,+919822987654,contact@goldenkeyrealty.com,4.8`;
    setCsvText(sample);
    setImportCampaignName("Pan-India Real Estate Scrape Pilot");
  };

  // Save Telegram Settings & Test
  const handleSaveTelegram = async () => {
    try {
      const res = await fetch("/api/crm/telegram", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          botToken: telegramBotToken,
          chatId: telegramChatId,
        }),
      });
      if (res.ok) {
        setTelegramTestResult("Telegram settings saved!");
        fetchData();
      }
    } catch (err: any) {
      setTelegramTestResult(`Error: ${err.message}`);
    }
  };

  const handleTestTelegram = async () => {
    setTelegramTesting(true);
    setTelegramTestResult(null);
    try {
      const res = await fetch("/api/crm/telegram", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "test",
          botToken: telegramBotToken || undefined,
          chatId: telegramChatId || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTelegramTestResult("✅ Success! Check your phone on Telegram!");
        fetchData();
      } else {
        setTelegramTestResult(`⚠️ Test failed: ${data.error}`);
      }
    } catch (err: any) {
      setTelegramTestResult(`❌ Network error: ${err.message}`);
    } finally {
      setTelegramTesting(false);
    }
  };

  const [detectingChatId, setDetectingChatId] = useState(false);

  const handleDetectChatId = async () => {
    if (!telegramBotToken.trim()) {
      alert("Please enter your Telegram Bot Token first!");
      return;
    }
    setDetectingChatId(true);
    setTelegramTestResult("Checking Telegram for your recent message...");
    try {
      const res = await fetch("/api/crm/telegram", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "detect_chat_id",
          botToken: telegramBotToken.trim(),
        }),
      });
      const data = await res.json();
      if (data.success && data.chatId) {
        setTelegramChatId(data.chatId);
        setTelegramTestResult(`✅ Found Chat ID for ${data.userName || "you"}: ${data.chatId}! Click "Save Settings" or "Send Test Ping".`);
      } else {
        setTelegramTestResult(`⚠️ ${data.error || "Could not detect Chat ID"}`);
      }
    } catch (err: any) {
      setTelegramTestResult(`❌ Error: ${err.message}`);
    } finally {
      setDetectingChatId(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner: Status & Overview */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 border border-blue-800/40 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-semibold uppercase tracking-wider mb-2">
              <Bot className="w-3.5 h-3.5" />
              Autonomous AI Sales Rep
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Multi-Industry Autonomous AI Sales Rep</h2>
            <p className="text-sm text-slate-300 max-w-2xl mt-1">
              Automates prospecting from imported leads to tailored industry pitches, live demos, and invoices with 
              <span className="text-blue-400 font-semibold"> 1-Click Human Approval</span> before any message leaves the system.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <Button
              onClick={() => setIsTelegramModalOpen(true)}
              variant="outline"
              className="bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs flex items-center gap-1.5"
            >
              <Bell className="w-3.5 h-3.5 text-amber-400" />
              Telegram Alerts
              {telegramStatus?.isEnabled && (
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              )}
            </Button>

            <Button
              onClick={handleReclassifyAll}
              disabled={reclassifying}
              variant="outline"
              className="bg-slate-800/80 border-slate-700 text-amber-300 hover:bg-slate-700 text-xs flex items-center gap-1.5"
              title="Re-detect industries and re-generate tailored pitches for all pending leads"
            >
              <Sparkles className={`w-3.5 h-3.5 text-amber-400 ${reclassifying ? "animate-spin" : ""}`} />
              {reclassifying ? "Refreshing Pitches..." : "Refresh Industry Pitches"}
            </Button>

            <Button
              onClick={() => setIsImportModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg shadow-blue-600/30"
            >
              <ArrowDownToLine className="w-4 h-4" />
              Import CSV
            </Button>

            {stats.approvedCount > 0 && (
              <Button
                onClick={handleDispatchApproved}
                disabled={dispatching}
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 animate-pulse"
              >
                <Send className="w-3.5 h-3.5" />
                {dispatching 
                  ? "Dispatching..." 
                  : stats.sentCount > 0 
                  ? `Resume Outreach (${stats.approvedCount} Remaining)` 
                  : `Launch Outreach (${stats.approvedCount})`}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-slate-800">
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-slate-700/50">
            <span className="text-xs text-slate-400 font-medium">Pending Your Approval</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-black text-amber-400">{stats.pendingCount}</span>
              {stats.pendingCount > 0 && (
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-300 font-semibold uppercase">
                  Action Needed
                </span>
              )}
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-slate-700/50">
            <span className="text-xs text-slate-400 font-medium">Approved & Queued</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-black text-emerald-400">{stats.approvedCount}</span>
              <span className="text-xs text-slate-400">Ready to Send</span>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-slate-700/50">
            <span className="text-xs text-slate-400 font-medium">Dispatched to Prospects</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-black text-blue-400">{stats.sentCount}</span>
              <span className="text-xs text-slate-400">WhatsApp / Email</span>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-slate-700/50">
            <span className="text-xs text-slate-400 font-medium">Total Ingested Leads</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-black text-slate-200">{stats.totalLeads}</span>
              <span className="text-xs text-slate-400">All Industries</span>
            </div>
          </div>
        </div>

        {/* WhatsApp Free-Tier Safety Meter (Strict 950 Monthly Cap) */}
        <div className="mt-4 p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-inner">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${whatsAppQuota.isLimitReached || whatsAppQuota.sentThisMonth >= 950 ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"}`}>
              <MessageCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-200">
                  WhatsApp Monthly Safety Quota ({whatsAppQuota.monthName})
                </span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                  whatsAppQuota.isLimitReached || whatsAppQuota.sentThisMonth >= 950
                    ? "bg-rose-500/20 text-rose-300 border border-rose-500/30" 
                    : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                }`}>
                  {whatsAppQuota.sentThisMonth} / 950 Used • {Math.max(0, 950 - whatsAppQuota.sentThisMonth)} Free Remaining
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {whatsAppQuota.isLimitReached || whatsAppQuota.sentThisMonth >= 950
                  ? "⚠️ Strict safety cap of 950 reached. WhatsApp is paused to guarantee 100% free Meta tier with zero extra charges! Resets next month."
                  : "🛡️ Hard restriction active: WhatsApp automatically stops at 950/month to guarantee 100% free Meta tier with zero extra charges (Meta free limit: 1,000)."}
              </p>
            </div>
          </div>

          <div className="w-full sm:w-44 self-end sm:self-center">
            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden border border-slate-700">
              <div
                className={`h-full transition-all duration-500 ${
                  whatsAppQuota.isLimitReached || whatsAppQuota.sentThisMonth >= 950
                    ? "bg-rose-500"
                    : whatsAppQuota.sentThisMonth > 800
                    ? "bg-amber-400"
                    : "bg-emerald-400"
                }`}
                style={{
                  width: `${Math.min(100, Math.max(3, (whatsAppQuota.sentThisMonth / 950) * 100))}%`,
                }}
              />
            </div>
            <div className="flex justify-between items-center text-[10px] text-slate-400 mt-1 font-mono">
              <span>{whatsAppQuota.sentThisMonth} sent</span>
              <span>Cap: 950</span>
            </div>
          </div>
        </div>

        {/* Meta Template Review Status Notice */}
        <div className="mt-4 p-3.5 rounded-xl bg-slate-900/90 border border-slate-700/80 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-inner">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${templateInfo.status === "APPROVED" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
              <MessageCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-200">
                  Meta WhatsApp Template: <span className="font-mono text-blue-300">{templateInfo.name}</span>
                </span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                  templateInfo.status === "APPROVED"
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                    : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                }`}>
                  {templateInfo.status === "APPROVED" ? "✅ APPROVED BY META" : "⏳ PENDING META APPROVAL"}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {templateInfo.status === "APPROVED"
                  ? "Meta has approved this template. Bulk automated API outreach to cold prospects is active!"
                  : "Meta is reviewing this template for cold outreach. In the meantime, use the emerald [💬 Chat on WA] button on each lead card for 100% peer-to-peer delivery!"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-start md:self-center">
            <button
              onClick={fetchTemplateStatus}
              disabled={templateInfo.loading}
              className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold px-2.5 py-1.5 rounded-lg border border-slate-700 flex items-center gap-1.5 transition-all"
              title="Refresh live template status from Meta"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${templateInfo.loading ? "animate-spin text-blue-400" : "text-slate-400"}`} />
              Check Status
            </button>
            <a
              href={templateInfo.directUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs bg-blue-600 hover:bg-blue-500 text-white font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-xs"
              title="View in Meta WhatsApp Manager (Message templates > Manage templates)"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Meta Templates Manager
            </a>
          </div>
        </div>

        {dispatchResult && (
          <div className="mt-4 p-3 rounded-lg bg-emerald-950/80 border border-emerald-700/50 text-emerald-300 text-xs flex items-center justify-between">
            <span>{dispatchResult}</span>
            <button onClick={() => setDispatchResult(null)} className="text-emerald-400 hover:text-white text-xs">
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Filter and Control Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => handleFilterChange("pending_approval")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              activeFilter === "pending_approval"
                ? "bg-amber-500 text-white shadow-sm"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
            }`}
          >
            ⏳ Pending Review ({stats.pendingCount})
          </button>

          <button
            onClick={() => handleFilterChange("approved")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              activeFilter === "approved"
                ? "bg-emerald-600 text-white shadow-sm"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
            }`}
          >
            ✅ Approved ({stats.approvedCount})
          </button>

          <button
            onClick={() => handleFilterChange("sent")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              activeFilter === "sent"
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
            }`}
          >
            📤 Dispatched ({stats.sentCount})
          </button>

          <button
            onClick={() => handleFilterChange("ALL")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
              activeFilter === "ALL"
                ? "bg-slate-800 text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
            }`}
          >
            All ({stats.totalLeads})
          </button>
        </div>

        {/* Right Tools: Search & Bulk Action */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search agency, city, phone..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <Button
            onClick={fetchData}
            variant="outline"
            className="p-2 h-auto text-xs text-slate-600 dark:text-slate-300"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>

          {activeFilter === "pending_approval" && stats.pendingCount > 0 && selectedIds.size === 0 && (
            <Button
              onClick={handleApproveAll}
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold whitespace-nowrap px-3 py-1.5 flex items-center gap-1 shadow-sm"
            >
              <Check className="w-3.5 h-3.5" />
              Approve All ({stats.pendingCount})
            </Button>
          )}
        </div>
      </div>

      {/* Multi-Page Selection Bar & Actions */}
      <div className="bg-slate-100 dark:bg-slate-900/80 p-3 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shadow-xs">
        {/* Left: Select Page & Select All Whole Queue & Deselect */}
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Checkbox for Select Page */}
          <button
            type="button"
            onClick={toggleSelectCurrentPage}
            disabled={items.length === 0}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all border ${
              isCurrentPageAllSelected
                ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                : hasSomeCurrentPageSelected
                ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700"
                : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
            title="Select or unselect all leads on this current page"
          >
            {isCurrentPageAllSelected ? (
              <CheckSquare className="w-3.5 h-3.5" />
            ) : (
              <Square className="w-3.5 h-3.5" />
            )}
            Select Page ({items.length})
          </button>

          {/* Select All Whole Queue (across all pages) */}
          <button
            type="button"
            onClick={handleSelectAllWholeQueue}
            disabled={isSelectingAllWhole || totalCount === 0}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900/60 transition-all flex items-center gap-1.5 shadow-xs"
            title="Select all leads matching the current filter across ALL pages"
          >
            <Sparkles className={`w-3.5 h-3.5 text-blue-500 ${isSelectingAllWhole ? "animate-spin" : ""}`} />
            {isSelectingAllWhole ? "Selecting all..." : `Select All ${totalCount.toLocaleString()} in Queue`}
          </button>

          {/* Deselect All (only for all) */}
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={handleDeselectAll}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 transition-all flex items-center gap-1.5 shadow-xs"
              title="Deselect all leads across all pages"
            >
              <XCircle className="w-3.5 h-3.5 text-rose-500" />
              Deselect All ({selectedIds.size.toLocaleString()})
            </button>
          )}
        </div>

        {/* Right: Actions on Selected Leads */}
        {selectedIds.size > 0 ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-950/80 px-2.5 py-1 rounded-lg border border-blue-200 dark:border-blue-800">
              {selectedIds.size.toLocaleString()} Selected
            </span>

            <Button
              onClick={handleApproveSelected}
              disabled={approvingSelected || dispatchingSelected}
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 shadow-sm"
              title="Approve all selected leads across all pages"
            >
              <Check className={`w-3.5 h-3.5 ${approvingSelected ? "animate-spin" : ""}`} />
              {approvingSelected ? "Approving..." : `Approve Selected (${selectedIds.size.toLocaleString()})`}
            </Button>

            <Button
              onClick={handleDispatchSelected}
              disabled={approvingSelected || dispatchingSelected}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 shadow-sm"
              title="Approve & dispatch outreach to all selected leads"
            >
              <Send className={`w-3.5 h-3.5 ${dispatchingSelected ? "animate-spin" : ""}`} />
              {dispatchingSelected ? "Dispatching..." : `Send to Selected (${selectedIds.size.toLocaleString()})`}
            </Button>
          </div>
        ) : (
          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
            <span>Page {currentPage} of {totalPages}</span>
            <span>•</span>
            <span>{totalCount.toLocaleString()} Total Leads in View</span>
          </div>
        )}
      </div>

      {/* Leads Approval Queue List */}
      {loading ? (
        <div className="py-16 text-center text-slate-500 flex flex-col items-center justify-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-xs uppercase font-semibold tracking-wider">Loading Outreach Queue...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">
          <Building2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">No Outreach Leads Found</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1 mb-4">
            Upload your scraped Google Maps CSV file or click below to load sample leads to test the workflow.
          </p>
          <Button
            onClick={() => {
              loadSampleCsv();
              setIsImportModalOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
          >
            <UploadCloud className="w-4 h-4 mr-1.5" />
            Upload Leads CSV
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
          {items.map((item) => {
            const isExpanded = expandedItemId === item.id;
            const isFounderTestCard = Boolean(
              (item.phone && item.phone.includes("9620194983")) ||
              (item.email && item.email.includes("johnsondev02")) ||
              item.business_name.includes("Founder Test Lead")
            );

            return (
              <div
                key={item.id}
                className={`rounded-xl border shadow-sm transition-all overflow-hidden ${
                  selectedIds.has(item.id)
                    ? "bg-blue-50/50 dark:bg-blue-950/30 border-blue-400 dark:border-blue-600 ring-2 ring-blue-500/40 shadow-md"
                    : isFounderTestCard
                    ? "bg-purple-50/50 dark:bg-purple-950/30 border-purple-400 dark:border-purple-600 ring-2 ring-purple-500/30 shadow-md"
                    : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-400/50"
                }`}
              >
                {/* Main Card Header */}
                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-start md:items-center gap-3 flex-1 min-w-0">
                    {/* Individual Card Checkbox */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectOne(item.id);
                      }}
                      className={`shrink-0 mt-0.5 md:mt-0 p-1 rounded-md border transition-all ${
                        selectedIds.has(item.id)
                          ? "bg-blue-600 border-blue-600 text-white shadow-xs"
                          : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-transparent hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }`}
                      title={selectedIds.has(item.id) ? "Deselect this lead" : "Select this lead"}
                    >
                      <Check className={`w-3.5 h-3.5 ${selectedIds.has(item.id) ? "opacity-100 text-white" : "opacity-0"}`} />
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-slate-900 dark:text-slate-100 text-sm flex items-center gap-1.5">
                          {item.business_name.replace(/\s*[·•\-]\s*Visited link/gi, "").replace(/Visited link/gi, "").trim()}
                        </span>
                      {isFounderTestCard && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-600 text-white shadow-xs animate-pulse">
                          <Sparkles className="w-3 h-3 text-amber-300" />
                          Founder Test Lead
                        </span>
                      )}
                      {item.city && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {item.city}
                        </span>
                      )}
                      {item.google_rating && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 font-semibold">
                          ★ {item.google_rating}
                        </span>
                      )}
                      {item.category && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-medium border border-blue-200/60 dark:border-blue-800/50">
                          <Tag className="w-3 h-3 text-blue-500" />
                          {item.category}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          item.status === "pending_approval"
                            ? "bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-300/40"
                            : item.status === "approved"
                            ? "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300/40"
                            : item.status === "sent"
                            ? "bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-300/40"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {item.status.replace("_", " ")}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mt-1 flex-wrap">
                      {item.phone && item.phone.length <= 16 && !item.phone.includes("++") && /^\+?[0-9\s-]{8,16}$/.test(item.phone) && (
                        <span className="flex items-center gap-1 font-mono">
                          <Phone className="w-3 h-3 text-emerald-500" />
                          {item.phone}
                        </span>
                      )}
                      {item.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-blue-500" />
                          {item.email}
                        </span>
                      )}
                      {item.channel_dispatched && (
                        <span className="text-[11px] text-blue-600 dark:text-blue-400 font-medium">
                          via {item.channel_dispatched}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions Toolbar */}
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                    {/* Fix Industry Pitch Button */}
                    <button
                      onClick={() => handleRegenerateSinglePitch(item.id)}
                      disabled={regeneratingId === item.id}
                      className="shrink-0 whitespace-nowrap text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 flex items-center gap-1.5 transition-all shadow-2xs"
                      title="Re-detect industry from business name and re-generate tailored pitch"
                    >
                      <Sparkles className={`w-3.5 h-3.5 text-amber-500 ${regeneratingId === item.id ? "animate-spin" : ""}`} />
                      {regeneratingId === item.id ? "Updating..." : "Fix Pitch"}
                    </button>

                    {/* Test on My Phone Button */}
                    <button
                      onClick={() => handleSendTestToMe(item)}
                      disabled={testingItemId === item.id}
                      className="shrink-0 whitespace-nowrap text-xs bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/40 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 font-semibold px-2.5 py-1.5 rounded-lg border border-purple-200 dark:border-purple-800 flex items-center gap-1.5 transition-all shadow-xs"
                      title="Send test pitch to +91 9620194983 & johnsondev02@gmail.com"
                    >
                      {testingItemId === item.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-500" />
                      ) : (
                        <FlaskConical className="w-3.5 h-3.5 text-purple-500" />
                      )}
                      Test on My Phone
                    </button>

                    {/* Direct WhatsApp Web / App Launch (Peer-to-peer instant 100% delivery) */}
                    {item.phone && item.phone.length <= 16 && !item.phone.includes("++") && /^\+?[0-9\s-]{8,16}$/.test(item.phone) && (
                      <a
                        href={`https://api.whatsapp.com/send?phone=${item.phone.replace(/\D/g, "")}&text=${encodeURIComponent(item.whatsapp_pitch || "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 whitespace-nowrap text-xs bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-semibold px-2.5 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800 flex items-center gap-1.5 transition-all shadow-xs"
                        title="Chat directly in WhatsApp Web / App (100% direct peer-to-peer delivery to prospect)"
                      >
                        <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                        Chat on WA
                      </a>
                    )}

                    {/* Single Dispatch Button - Always available */}
                    <button
                      onClick={() => handleDispatchSingleLead(item)}
                      disabled={dispatchingSingleId === item.id}
                      className={`shrink-0 whitespace-nowrap text-xs font-semibold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-xs ${
                        item.status === "sent" || item.status === "replied"
                          ? "bg-slate-700 hover:bg-slate-600 text-white"
                          : "bg-emerald-600 hover:bg-emerald-500 text-white"
                      }`}
                      title={item.status === "sent" || item.status === "replied" ? "Dispatch again to this agency" : "Dispatch live pitch to this agency"}
                    >
                      {dispatchingSingleId === item.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <SendHorizontal className="w-3.5 h-3.5" />
                      )}
                      {item.status === "sent" || item.status === "replied" ? "Re-dispatch (1)" : "Dispatch (1)"}
                    </button>

                    {/* Reset to Approved button if already sent or replied */}
                    {(item.status === "sent" || item.status === "replied") && (
                      <button
                        onClick={() => handleResetToApproved(item.id)}
                        className="shrink-0 whitespace-nowrap text-xs text-amber-600 dark:text-amber-400 hover:underline px-2 py-1.5 font-medium"
                        title="Move back to Approved queue"
                      >
                        Reset to Approved
                      </button>
                    )}

                    {/* Expand/Collapse Toggle */}
                    <button
                      onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                      className="shrink-0 whitespace-nowrap text-xs text-slate-600 dark:text-slate-300 hover:text-blue-500 font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1.5"
                    >
                      {isExpanded ? "Hide Pitch" : "View / Edit"}
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>

                    {item.status === "pending_approval" && (
                      <>
                        <Button
                          onClick={() => handleApprove(item.id)}
                          className="shrink-0 whitespace-nowrap bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-3 py-1.5 h-auto flex items-center gap-1.5"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Approve
                        </Button>
                        <button
                          onClick={() => handleReject(item.id)}
                          className="shrink-0 whitespace-nowrap text-xs text-slate-400 hover:text-rose-500 px-2 py-1.5 font-medium"
                          title="Skip Lead"
                        >
                          Skip
                        </button>
                      </>
                    )}

                    {item.status === "approved" && (
                      <span className="shrink-0 whitespace-nowrap text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1 px-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Approved
                      </span>
                    )}
                  </div>
                </div>

                {/* Expanded Pitch Viewer & Inline Editor */}
                {isExpanded && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-950/70 border-t border-slate-200 dark:border-slate-800 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* WhatsApp Message Editor */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" />
                            WhatsApp Message Pitch (Directly Editable)
                          </span>
                          <button
                            onClick={() => {
                              const text = editingCardPitches[item.id]?.whatsapp_pitch ?? (item.whatsapp_pitch || "");
                              if (text) navigator.clipboard.writeText(text);
                              alert("WhatsApp pitch copied!");
                            }}
                            className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                        </div>

                        <textarea
                          rows={10}
                          value={
                            editingCardPitches[item.id]?.whatsapp_pitch !== undefined
                              ? editingCardPitches[item.id].whatsapp_pitch
                              : (item.whatsapp_pitch || "")
                          }
                          onChange={(e) => {
                            const current = editingCardPitches[item.id] || {
                              whatsapp_pitch: item.whatsapp_pitch || "",
                              email_subject: item.email_subject || "",
                              email_pitch: item.email_pitch || "",
                            };
                            setEditingCardPitches({
                              ...editingCardPitches,
                              [item.id]: { ...current, whatsapp_pitch: e.target.value },
                            });
                          }}
                          className="w-full text-xs font-sans p-3 rounded-lg border border-emerald-300/80 dark:border-emerald-800/60 bg-emerald-50/20 dark:bg-emerald-950/10 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 leading-relaxed font-mono"
                          placeholder="Type WhatsApp message here..."
                        />
                      </div>

                      {/* Email Pitch Editor & Preview */}
                      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5" />
                            B2B Email Pitch & Subject (Editable)
                          </span>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => {
                                const html = editingCardPitches[item.id]?.email_pitch ?? (item.email_pitch || "");
                                const tempEl = document.createElement("div");
                                tempEl.innerHTML = html;
                                const plain = tempEl.innerText || tempEl.textContent || html;
                                navigator.clipboard.writeText(plain.trim());
                                alert("Plain text email copied! Ready to paste into Gmail / Outlook.");
                              }}
                              className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                            >
                              <Copy className="w-3 h-3" /> Copy Text
                            </button>
                            <button
                              onClick={() => {
                                const html = editingCardPitches[item.id]?.email_pitch ?? (item.email_pitch || "");
                                if (html) navigator.clipboard.writeText(html);
                                alert("Email template HTML copied!");
                              }}
                              className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1"
                            >
                              Copy HTML
                            </button>
                            <a
                              href={item.demo_link || "/demos/real-estate"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] text-blue-500 hover:underline flex items-center gap-1 font-medium"
                            >
                              Test Demo <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>

                        {/* Subject Input */}
                        <div className="mb-2">
                          <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                            Subject Line:
                          </label>
                          <input
                            type="text"
                            value={
                              editingCardPitches[item.id]?.email_subject !== undefined
                                ? editingCardPitches[item.id].email_subject
                                : (item.email_subject || "")
                            }
                            onChange={(e) => {
                              const current = editingCardPitches[item.id] || {
                                whatsapp_pitch: item.whatsapp_pitch || "",
                                email_subject: item.email_subject || "",
                                email_pitch: item.email_pitch || "",
                              };
                              setEditingCardPitches({
                                ...editingCardPitches,
                                [item.id]: { ...current, email_subject: e.target.value },
                              });
                            }}
                            className="w-full text-xs px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>

                        {/* Visual HTML Preview / Code */}
                        <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 h-52 shadow-inner">
                          <iframe
                            srcDoc={editingCardPitches[item.id]?.email_pitch ?? (item.email_pitch || "")}
                            title="Email Preview"
                            className="w-full h-full border-0"
                            sandbox="allow-same-origin"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Bottom Action Bar on Every Card */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 border-t border-slate-200 dark:border-slate-800">
                      <div className="flex items-center gap-2">
                        {savedNoticeId === item.id ? (
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 animate-pulse bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1 rounded-full border border-emerald-300/40">
                            <CheckCircle className="w-3.5 h-3.5" />
                            WhatsApp & Email Saved Successfully!
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">
                            💡 You can edit copy above and click "Save Pitch" before sending.
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Save Button */}
                        <Button
                          onClick={() => handleSaveCardPitch(item)}
                          className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-3 py-1.5 h-auto flex items-center gap-1.5 shadow-sm"
                        >
                          <Save className="w-3.5 h-3.5" />
                          Save WhatsApp & Email
                        </Button>

                        {/* Send Test to Me Button */}
                        <Button
                          onClick={() => handleSendTestToMe(item)}
                          disabled={testingItemId === item.id}
                          variant="outline"
                          className="border-purple-300 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40 text-xs font-semibold px-3 py-1.5 h-auto flex items-center gap-1.5"
                        >
                          {testingItemId === item.id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <FlaskConical className="w-3.5 h-3.5 text-purple-500" />
                          )}
                          Send Test to My Phone (+91 9620194983)
                        </Button>

                        {/* Single Dispatch Button - Always available */}
                        <Button
                          onClick={() => handleDispatchSingleLead(item)}
                          disabled={dispatchingSingleId === item.id}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 h-auto flex items-center gap-1.5 shadow-sm"
                        >
                          {dispatchingSingleId === item.id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <SendHorizontal className="w-3.5 h-3.5" />
                          )}
                          {item.status === "sent" || item.status === "replied" ? "Re-dispatch Live Pitch" : "Dispatch Only This Lead"}
                        </Button>

                        {/* Reset to Approved in bottom bar */}
                        {(item.status === "sent" || item.status === "replied") && (
                          <Button
                            onClick={() => handleResetToApproved(item.id)}
                            variant="outline"
                            className="text-xs text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-800 px-2.5 py-1.5 h-auto"
                          >
                            Reset to Approved Queue
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
            <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-3 flex-wrap">
              <span>
                Showing <strong className="text-slate-900 dark:text-slate-100">{Math.min(totalCount, (currentPage - 1) * pageSize + 1)}</strong> to{" "}
                <strong className="text-slate-900 dark:text-slate-100">{Math.min(totalCount, currentPage * pageSize)}</strong> of{" "}
                <strong className="text-slate-900 dark:text-slate-100">{totalCount.toLocaleString()}</strong> leads
              </span>

              {/* Page Size Selector */}
              <div className="flex items-center gap-1.5 pl-2 border-l border-slate-300 dark:border-slate-700">
                <span className="text-slate-400">Show:</span>
                {[25, 50, 100, 200].map((size) => (
                  <button
                    key={size}
                    onClick={() => handlePageSizeChange(size)}
                    className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                      pageSize === size
                        ? "bg-blue-600 text-white shadow-xs"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    {size}
                  </button>
                ))}
                <span className="text-slate-400">per page</span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all shadow-2xs"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center gap-1 text-xs font-semibold px-2.5 shadow-2xs"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </button>

              {/* Current Page Indicator / Jump */}
              <div className="flex items-center gap-1.5 px-2 text-xs font-bold text-slate-700 dark:text-slate-300">
                <span>Page</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= totalPages) {
                      setCurrentPage(val);
                    }
                  }}
                  className="w-14 text-center py-1 rounded border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 font-mono text-xs focus:ring-1 focus:ring-blue-500"
                />
                <span>of {totalPages.toLocaleString()}</span>
              </div>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center gap-1 text-xs font-semibold px-2.5 shadow-2xs"
                title="Next Page"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all shadow-2xs"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </>
      )}

      {/* CSV Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-blue-500" />
                  Import Leads CSV (All Industries)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Upload your scraped leads CSV. The Autonomous AI automatically detects each industry (Restaurants, Cafes, Real Estate, Clinics, Gyms, Salons, etc.) and synthesizes tailored pitches.
                </p>
              </div>
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Campaign / Batch Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Bangalore Multi-Industry Leads Master"
                  value={importCampaignName}
                  onChange={(e) => setImportCampaignName(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Tab Selector: Upload File vs Paste Text */}
              <div className="flex items-center gap-2 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setImportTab("upload")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    importTab === "upload"
                      ? "bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                  }`}
                >
                  <UploadCloud className="w-3.5 h-3.5" />
                  Upload / Drag &amp; Drop CSV File
                </button>
                <button
                  type="button"
                  onClick={() => setImportTab("paste")}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                    importTab === "paste"
                      ? "bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-white"
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  Paste CSV Text
                </button>
              </div>

              {importTab === "upload" ? (
                <div>
                  <input
                    type="file"
                    id="csv-file-input"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleFileRead(e.target.files[0]);
                      }
                    }}
                  />
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                        handleFileRead(e.dataTransfer.files[0]);
                      }
                    }}
                    onClick={() => document.getElementById("csv-file-input")?.click()}
                    className={`border-2 border-dashed rounded-2xl p-7 text-center cursor-pointer transition-all ${
                      isDragging
                        ? "border-blue-500 bg-blue-50/70 dark:bg-blue-950/50 scale-[1.01]"
                        : uploadedFileName
                        ? "border-emerald-500/70 bg-emerald-50/40 dark:bg-emerald-950/30"
                        : "border-slate-300 dark:border-slate-700 hover:border-blue-400 bg-slate-50/60 dark:bg-slate-800/40"
                    }`}
                  >
                    {uploadedFileName ? (
                      <div className="space-y-2">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-sm">
                          <CheckCircle className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{uploadedFileName}</p>
                        <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-300/40">
                          ✨ {parsedLeadPreviewCount} Leads Successfully Recognized!
                        </span>
                        <p className="text-[11px] text-slate-400 mt-1">Click or drag another .csv file to replace</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto shadow-sm">
                          <UploadCloud className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                          Drag &amp; drop your leads .csv file here
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          or <span className="text-blue-600 dark:text-blue-400 font-semibold underline">browse from your computer</span>
                        </p>
                        <p className="text-[11px] text-slate-400">Supports standard CRM leads and exported files from Google Maps or scrapers</p>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-2 px-1">
                    <a
                      href="/api/crm/leads/sample-csv?profile=outreach"
                      download="ai_sales_rep_prospects_template.csv"
                      className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                      title="Download sample CSV for AI Sales Rep"
                    >
                      <Download className="w-3 h-3" />
                      <span>Download Prospects Template (.csv)</span>
                    </a>
                    <button
                      type="button"
                      onClick={loadSampleCsv}
                      className="text-[11px] text-blue-500 hover:underline font-medium"
                    >
                      Or load sample leads to test
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                      Paste CSV Content
                    </label>
                    <button
                      type="button"
                      onClick={loadSampleCsv}
                      className="text-[11px] text-blue-500 hover:underline font-medium"
                    >
                      Paste Sample Leads
                    </button>
                  </div>
                  <textarea
                    rows={8}
                    placeholder={`"hfpxzc href","xxVWCe","MW4etd","UsdlK",...\nPaste your raw scraped CSV lines here`}
                    value={csvText}
                    onChange={(e) => {
                      setCsvText(e.target.value);
                      const parsed = parseGoogleMapsCsv(e.target.value);
                      setParsedLeadPreviewCount(parsed.length);
                    }}
                    className="w-full text-xs font-mono p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {parsedLeadPreviewCount > 0 && (
                    <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium mt-1 inline-block">
                      ✨ {parsedLeadPreviewCount} leads detected in pasted text
                    </span>
                  )}
                </div>
              )}

              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/50 rounded-xl p-3 text-xs text-blue-700 dark:text-blue-300">
                💡 <strong>Telegram & PWA Alert:</strong> Once processed, you will get an instant push notification on Telegram so you can review and approve before any messages are sent!
              </div>
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-2">
              <Button
                onClick={() => setIsImportModalOpen(false)}
                variant="outline"
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProcessCsvImport}
                disabled={importing}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
              >
                {importing ? "Generating AI Pitches..." : "Generate Pitches & Queue"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Configuration Modal */}
      {isTelegramModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Telegram Push Notification Setup
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Receive instant alerts on your phone whenever leads need approval or respond.
                </p>
              </div>
              <button
                onClick={() => setIsTelegramModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-600 dark:text-slate-300 space-y-1.5">
                <p className="font-bold text-slate-800 dark:text-slate-200">How to get your credentials in 60 seconds:</p>
                <ol className="list-decimal list-inside space-y-1 text-[11px]">
                  <li>Open Telegram and search for <strong>@BotFather</strong></li>
                  <li>Send <code>/newbot</code>, choose a name (e.g. <em>JohnsonSalesBot</em>), and copy the <strong>HTTP API Token</strong></li>
                  <li>Search for <strong>@userinfobot</strong> on Telegram and copy your <strong>Id</strong> (Chat ID)</li>
                  <li>Paste them below and click <strong>Send Test Ping</strong>!</li>
                </ol>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Telegram Bot Token
                </label>
                <input
                  type="password"
                  placeholder="e.g. 123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                  value={telegramBotToken}
                  onChange={(e) => setTelegramBotToken(e.target.value)}
                  className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Your Personal Telegram Chat ID (Numbers only)
                  </label>
                  <button
                    type="button"
                    onClick={handleDetectChatId}
                    disabled={detectingChatId}
                    className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline font-bold flex items-center gap-1 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-900"
                  >
                    <Search className="w-3 h-3" />
                    {detectingChatId ? "Detecting..." : "⚡ Auto-Detect My Chat ID"}
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="e.g. 987654321"
                  value={telegramChatId}
                  onChange={(e) => setTelegramChatId(e.target.value)}
                  className="w-full text-xs font-mono px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  💡 <strong>Instant setup:</strong> Send a quick message (e.g. <code>hi</code>) to your bot on Telegram, then click <strong>Auto-Detect My Chat ID</strong> above!
                </p>
              </div>

              {telegramTestResult && (
                <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-medium">
                  {telegramTestResult}
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <Button
                onClick={handleTestTelegram}
                disabled={telegramTesting}
                variant="outline"
                className="text-xs flex items-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5 text-blue-500" />
                {telegramTesting ? "Sending..." : "Send Test Ping to Phone"}
              </Button>

              <Button
                onClick={handleSaveTelegram}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pitch Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-blue-500" />
                  Customize Pitch for {editingItem.business_name}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Fine-tune the copy before approving for dispatch.
                </p>
              </div>
              <button
                onClick={() => setEditingItem(null)}
                className="text-slate-400 hover:text-slate-600 text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  WhatsApp Message Pitch
                </label>
                <textarea
                  rows={6}
                  value={editingItem.whatsapp_pitch || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, whatsapp_pitch: e.target.value })}
                  className="w-full text-xs font-sans p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Email Subject Line
                </label>
                <input
                  type="text"
                  value={editingItem.email_subject || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, email_subject: e.target.value })}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Email HTML / Body Template
                </label>
                <textarea
                  rows={8}
                  value={editingItem.email_pitch || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, email_pitch: e.target.value })}
                  className="w-full text-xs font-mono p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-2">
              <Button
                onClick={() => setEditingItem(null)}
                variant="outline"
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePitchEdit}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
              >
                Save & Update
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
